import { useState } from "react";
import { Bell, LogOut, Shield, ChevronRight, Settings, HelpCircle, CreditCard, Trash2, X } from "lucide-react";
import { Link } from "wouter";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest, queryClient } from "@/lib/queryClient";

function getInitials(user: any): string {
  const name = user?.firstName || user?.name || user?.username || "";
  const email = user?.email || "";
  if (name) {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase() || "??";
}

function getDisplayName(user: any): string {
  if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
  if (user?.firstName) return user.firstName;
  if (user?.name) return user.name;
  if (user?.username) return user.username;
  return "Surfer";
}

export default function Profile() {
  const { user, logout, planTier, isAgentLoading, isProLoading } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const initials = getInitials(user);
  const displayName = getDisplayName(user);
  const email = (user as any)?.email || "";

  const ITEMS = [
    { icon: CreditCard,  label: "Account & Billing", value: "",           color: "#a78bfa", href: "/account" },
    { icon: Bell,        label: "Notifications", value: "",              color: "#fbbf24", href: "/notifications" },
    { icon: Settings,    label: "Preferences",   value: "",              color: "#94a3b8", href: "/settings" },
    { icon: Shield,      label: "Privacy",        value: "",              color: "#38bdf8", href: "/privacy" },
    { icon: HelpCircle,  label: "Help & Support", value: "",              color: "#34d399", href: "/support" },
  ];

  const deleteAccount = async () => {
    if (confirmation !== "DELETE" || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiRequest("/api/account", {
        method: "DELETE",
        body: { confirmation },
      });
      queryClient.clear();
      try {
        await logout();
      } finally {
        window.location.assign("/");
      }
    } catch (error) {
      const raw = error instanceof Error ? error.message : "";
      try {
        const jsonStart = raw.indexOf("{");
        const payload = jsonStart >= 0 ? JSON.parse(raw.slice(jsonStart)) : null;
        setDeleteError(payload?.message || "We couldn't delete your account. Please try again.");
      } catch {
        setDeleteError("We couldn't delete your account. Please try again.");
      }
      setDeleting(false);
    }
  };

  return (
    <div className="ls-shell min-h-screen flex flex-col">
      <Header />
      <div className="px-5 pt-6 pb-6"
        style={{ background: "linear-gradient(180deg,#041a2e 0%,#030a14 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-black text-white flex-shrink-0 select-none"
              style={{ background: "linear-gradient(135deg,#065f46,#0c4a6e)", border: "2px solid rgba(52,211,153,0.35)" }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-white font-bold text-lg leading-tight">{displayName}</h1>
              {email && <p className="text-slate-600 text-[11px] mt-0.5 truncate">{email}</p>}
              <span className="inline-flex mt-1 rounded-full border border-emerald-300/20 bg-emerald-300/[.08] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-200">{isAgentLoading || isProLoading ? "Checking membership" : `${planTier} membership`}</span>
            </div>
          </div>

          <div className="mt-5" style={{ height: 1, background: "rgba(255,255,255,0.05)" }} />
        </div>
      </div>

      {/* ── Menu list ── */}
      <main className="flex-1 px-5 pb-8 max-w-3xl mx-auto w-full">
        <div className="space-y-1">
          {ITEMS.map(({ icon: Icon, label, value, color, href }) => {
            const row = (
              <div className="flex items-center justify-between px-3 py-3.5 rounded-xl cursor-pointer transition-colors hover:bg-white/[.035]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <div className="flex items-center gap-3.5">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${color}14`, border: `1px solid ${color}22` }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                  <span className="text-white text-[13px] font-medium">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {value ? <span className="text-slate-600 text-[11px]">{value}</span> : null}
                  <ChevronRight size={13} className="text-slate-700" />
                </div>
              </div>
            );

            return (
              <div key={label}>
                {href ? <Link href={href}>{row}</Link> : row}
              </div>
            );
          })}
        </div>

        {/* Sign out */}
        <div className="pt-5">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3.5 px-3 py-3.5 rounded-xl transition-colors hover:bg-red-400/[.06]"
            style={{ border: "1px solid rgba(239,68,68,0.15)" }}>
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.18)" }}>
              <LogOut size={14} className="text-red-400" />
            </div>
            <span className="text-red-400 text-[13px] font-medium">Sign Out</span>
          </button>
        </div>

        <section className="mt-8 rounded-2xl p-4" style={{ border: "1px solid rgba(239,68,68,0.18)", background: "rgba(127,29,29,0.08)" }}>
          <h2 className="text-red-300 text-[13px] font-bold">Delete account</h2>
          <p className="text-slate-500 text-[11px] leading-relaxed mt-1.5">
            Permanently delete your profile, saved spots, alerts, and account data. Any active LiveSwell subscription will be cancelled first.
          </p>
          <button
            onClick={() => { setDeleteOpen(true); setDeleteError(""); setConfirmation(""); }}
            className="mt-3 flex items-center gap-2 text-red-400 text-[12px] font-semibold"
          >
            <Trash2 size={13} /> Delete my account
          </button>
        </section>
      </main>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5" style={{ background: "rgba(0,0,0,0.82)" }} role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
          <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]" style={{ background: "#081421", border: "1px solid rgba(239,68,68,0.25)" }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-red-400 text-[10px] font-bold tracking-widest uppercase">Permanent action</p>
                <h2 id="delete-account-title" className="text-white text-xl font-black mt-1">Delete your account?</h2>
              </div>
              <button aria-label="Close" onClick={() => !deleting && setDeleteOpen(false)} className="text-slate-500 p-1"><X size={20} /></button>
            </div>
            <p className="text-slate-400 text-[12px] leading-relaxed mt-4">
              This cannot be undone. LiveSwell will cancel attached subscriptions, remove your sign-in, and permanently erase your profile, alerts, saved spots, and messaging data.
            </p>
            <label className="block text-slate-300 text-[11px] font-semibold mt-5">
              Type <span className="text-white">DELETE</span> to confirm
              <input
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoCapitalize="characters"
                autoComplete="off"
                disabled={deleting}
                className="mt-2 w-full rounded-xl px-4 py-3 text-white outline-none"
                style={{ background: "#030a14", border: "1px solid rgba(255,255,255,0.12)" }}
              />
            </label>
            {deleteError && <p className="text-red-300 text-[11px] leading-relaxed mt-3" role="alert">{deleteError}</p>}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <button onClick={() => setDeleteOpen(false)} disabled={deleting} className="rounded-xl py-3 text-slate-300 text-[12px] font-bold" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>Keep account</button>
              <button onClick={deleteAccount} disabled={confirmation !== "DELETE" || deleting} className="rounded-xl py-3 text-white text-[12px] font-bold disabled:opacity-40" style={{ background: "#dc2626" }}>{deleting ? "Deleting…" : "Delete forever"}</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
